import React, { useState } from 'react';
import { PlusIcon, Edit2Icon, Trash2Icon, PackageIcon, ChevronDownIcon, ChevronUpIcon, XIcon, CheckIcon, LayersIcon } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Toggle } from '../../components/ui/Toggle';
// ─── Types ───────────────────────────────────────────────────────────────────
interface PackageOption {
  id: string;
  name: string;
  description: string;
  included: boolean;
}
interface Tier {
  id: string;
  name: string;
  price: number;
  billingPeriod: 'monthly' | 'yearly' | 'one-time';
  isActive: boolean;
  options: PackageOption[];
}
interface Package {
  id: string;
  name: string;
  description: string;
  tiers: Tier[];
}
// ─── Seed Data ────────────────────────────────────────────────────────────────
const initialPackages: Package[] = [{
  id: '1',
  name: 'Starter',
  description: 'Perfect for small projects and individual builders',
  tiers: [{
    id: 't1',
    name: 'Basic',
    price: 29,
    billingPeriod: 'monthly',
    isActive: true,
    options: [{
      id: 'o1',
      name: 'Up to 5 projects',
      description: 'Manage up to 5 active projects',
      included: true
    }, {
      id: 'o2',
      name: 'Email support',
      description: 'Standard email support',
      included: true
    }, {
      id: 'o3',
      name: 'Analytics dashboard',
      description: 'Basic analytics',
      included: false
    }]
  }, {
    id: 't2',
    name: 'Pro',
    price: 79,
    billingPeriod: 'monthly',
    isActive: true,
    options: [{
      id: 'o4',
      name: 'Unlimited projects',
      description: 'No project limits',
      included: true
    }, {
      id: 'o5',
      name: 'Priority support',
      description: '24/7 priority support',
      included: true
    }, {
      id: 'o6',
      name: 'Analytics dashboard',
      description: 'Advanced analytics',
      included: true
    }]
  }]
}, {
  id: '2',
  name: 'Enterprise',
  description: 'For large teams and enterprise-scale deployments',
  tiers: [{
    id: 't3',
    name: 'Team',
    price: 199,
    billingPeriod: 'monthly',
    isActive: true,
    options: [{
      id: 'o7',
      name: 'Unlimited everything',
      description: 'No limits on any feature',
      included: true
    }, {
      id: 'o8',
      name: 'Dedicated account manager',
      description: 'Personal account manager',
      included: true
    }, {
      id: 'o9',
      name: 'Custom integrations',
      description: 'Build custom integrations',
      included: false
    }]
  }]
}];
// ─── Modal Overlay ────────────────────────────────────────────────────────────
function ModalOverlay({
  children,
  onClose
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-1831">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" data-id="element-1832">
        {children}
      </div>
    </div>;
}
function ModalHeader({
  title,
  onClose
}: {
  title: string;
  onClose: () => void;
}) {
  return <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" data-id="element-1833">
      <h2 className="text-base font-semibold text-gray-900" data-id="element-1834">{title}</h2>
      <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" data-id="element-1835">
        <XIcon size={16} data-id="element-1836" />
      </button>
    </div>;
}
// ─── Create Package Modal ─────────────────────────────────────────────────────
interface CreatePackageModalProps {
  onClose: () => void;
  onCreate: (name: string, description: string) => void;
}
function CreatePackageModal({
  onClose,
  onCreate
}: CreatePackageModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<{
    name?: string;
  }>({});
  const handleSubmit = () => {
    if (!name.trim()) {
      setErrors({
        name: 'Package name is required'
      });
      return;
    }
    onCreate(name.trim(), description.trim());
    onClose();
  };
  return <ModalOverlay onClose={onClose} data-id="element-1837">
      <ModalHeader title="Create Package" onClose={onClose} data-id="element-1838" />
      <div className="px-6 py-5 space-y-4" data-id="element-1839">
        <Input label="Package Name" placeholder="e.g. Starter, Professional, Enterprise" value={name} onChange={e => {
        setName(e.target.value);
        setErrors({});
      }} error={errors.name} data-id="element-1840" />
        <div className="space-y-1.5" data-id="element-1841">
          <label className="block text-sm font-medium text-gray-700" data-id="element-1842">
            Description
          </label>
          <textarea className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors placeholder:text-gray-400" rows={3} placeholder="Describe what this package offers..." value={description} onChange={e => setDescription(e.target.value)} data-id="element-1843" />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50" data-id="element-1844">
        <Button variant="ghost" size="sm" onClick={onClose} data-id="element-1845">
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleSubmit} data-id="element-1846">
          Create Package
        </Button>
      </div>
    </ModalOverlay>;
}
// ─── Add Tier Modal ───────────────────────────────────────────────────────────
interface AddTierModalProps {
  packageName: string;
  onClose: () => void;
  onAdd: (tier: Omit<Tier, 'id' | 'options'>) => void;
}
function AddTierModal({
  packageName,
  onClose,
  onAdd
}: AddTierModalProps) {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [billingPeriod, setBillingPeriod] = useState<Tier['billingPeriod']>('monthly');
  const [errors, setErrors] = useState<{
    name?: string;
    price?: string;
  }>({});
  const handleSubmit = () => {
    const newErrors: {
      name?: string;
      price?: string;
    } = {};
    if (!name.trim()) newErrors.name = 'Tier name is required';
    if (!price || isNaN(Number(price)) || Number(price) < 0) newErrors.price = 'Valid price is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onAdd({
      name: name.trim(),
      price: Number(price),
      billingPeriod,
      isActive: true
    });
    onClose();
  };
  return <ModalOverlay onClose={onClose} data-id="element-1847">
      <ModalHeader title={`Add Tier to "${packageName}"`} onClose={onClose} data-id="element-1848" />
      <div className="px-6 py-5 space-y-4" data-id="element-1849">
        <Input label="Tier Name" placeholder="e.g. Basic, Pro, Enterprise" value={name} onChange={e => {
        setName(e.target.value);
        setErrors(p => ({
          ...p,
          name: undefined
        }));
      }} error={errors.name} data-id="element-1850" />
        <div className="grid grid-cols-2 gap-3" data-id="element-1851">
          <Input label="Price" type="number" placeholder="0" value={price} onChange={e => {
          setPrice(e.target.value);
          setErrors(p => ({
            ...p,
            price: undefined
          }));
        }} error={errors.price} data-id="element-1852" />
          <div className="space-y-1.5" data-id="element-1853">
            <label className="block text-sm font-medium text-gray-700" data-id="element-1854">
              Billing Period
            </label>
            <select className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors bg-white" value={billingPeriod} onChange={e => setBillingPeriod(e.target.value as Tier['billingPeriod'])} data-id="element-1855">
              <option value="monthly" data-id="element-1856">Monthly</option>
              <option value="yearly" data-id="element-1857">Yearly</option>
              <option value="one-time" data-id="element-1858">One-time</option>
            </select>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50" data-id="element-1859">
        <Button variant="ghost" size="sm" onClick={onClose} data-id="element-1860">
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleSubmit} data-id="element-1861">
          Add Tier
        </Button>
      </div>
    </ModalOverlay>;
}
// ─── Edit Tier Modal ──────────────────────────────────────────────────────────
interface EditTierModalProps {
  tier: Tier;
  onClose: () => void;
  onSave: (updates: Partial<Tier>) => void;
}
function EditTierModal({
  tier,
  onClose,
  onSave
}: EditTierModalProps) {
  const [name, setName] = useState(tier.name);
  const [price, setPrice] = useState(String(tier.price));
  const [billingPeriod, setBillingPeriod] = useState<Tier['billingPeriod']>(tier.billingPeriod);
  const [isActive, setIsActive] = useState(tier.isActive);
  const [errors, setErrors] = useState<{
    name?: string;
    price?: string;
  }>({});
  const handleSubmit = () => {
    const newErrors: {
      name?: string;
      price?: string;
    } = {};
    if (!name.trim()) newErrors.name = 'Tier name is required';
    if (!price || isNaN(Number(price)) || Number(price) < 0) newErrors.price = 'Valid price is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSave({
      name: name.trim(),
      price: Number(price),
      billingPeriod,
      isActive
    });
    onClose();
  };
  return <ModalOverlay onClose={onClose} data-id="element-1862">
      <ModalHeader title={`Edit Tier — ${tier.name}`} onClose={onClose} data-id="element-1863" />
      <div className="px-6 py-5 space-y-4" data-id="element-1864">
        <Input label="Tier Name" placeholder="e.g. Basic, Pro, Enterprise" value={name} onChange={e => {
        setName(e.target.value);
        setErrors(p => ({
          ...p,
          name: undefined
        }));
      }} error={errors.name} data-id="element-1865" />
        <div className="grid grid-cols-2 gap-3" data-id="element-1866">
          <Input label="Price" type="number" placeholder="0" value={price} onChange={e => {
          setPrice(e.target.value);
          setErrors(p => ({
            ...p,
            price: undefined
          }));
        }} error={errors.price} data-id="element-1867" />
          <div className="space-y-1.5" data-id="element-1868">
            <label className="block text-sm font-medium text-gray-700" data-id="element-1869">
              Billing Period
            </label>
            <select className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors bg-white" value={billingPeriod} onChange={e => setBillingPeriod(e.target.value as Tier['billingPeriod'])} data-id="element-1870">
              <option value="monthly" data-id="element-1871">Monthly</option>
              <option value="yearly" data-id="element-1872">Yearly</option>
              <option value="one-time" data-id="element-1873">One-time</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg" data-id="element-1874">
          <span className="text-sm font-medium text-gray-700" data-id="element-1875">Active</span>
          <Toggle checked={isActive} onChange={setIsActive} label="" data-id="element-1876" />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50" data-id="element-1877">
        <Button variant="ghost" size="sm" onClick={onClose} data-id="element-1878">
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleSubmit} data-id="element-1879">
          Save Changes
        </Button>
      </div>
    </ModalOverlay>;
}
// ─── Add Option Modal ─────────────────────────────────────────────────────────
interface AddOptionModalProps {
  tierName: string;
  onClose: () => void;
  onAdd: (option: Omit<PackageOption, 'id'>) => void;
}
function AddOptionModal({
  tierName,
  onClose,
  onAdd
}: AddOptionModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [included, setIncluded] = useState(true);
  const [errors, setErrors] = useState<{
    name?: string;
  }>({});
  const handleSubmit = () => {
    if (!name.trim()) {
      setErrors({
        name: 'Option name is required'
      });
      return;
    }
    onAdd({
      name: name.trim(),
      description: description.trim(),
      included
    });
    onClose();
  };
  return <ModalOverlay onClose={onClose} data-id="element-1880">
      <ModalHeader title={`Add Option to "${tierName}"`} onClose={onClose} data-id="element-1881" />
      <div className="px-6 py-5 space-y-4" data-id="element-1882">
        <Input label="Option Name" placeholder="e.g. Unlimited projects, Priority support" value={name} onChange={e => {
        setName(e.target.value);
        setErrors({});
      }} error={errors.name} data-id="element-1883" />
        <div className="space-y-1.5" data-id="element-1884">
          <label className="block text-sm font-medium text-gray-700" data-id="element-1885">
            Description
          </label>
          <textarea className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors placeholder:text-gray-400" rows={2} placeholder="Brief description of this option..." value={description} onChange={e => setDescription(e.target.value)} data-id="element-1886" />
        </div>
        <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg" data-id="element-1887">
          <div data-id="element-1888">
            <p className="text-sm font-medium text-gray-700" data-id="element-1889">
              Included by default
            </p>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-1890">
              Whether this option is included in the tier
            </p>
          </div>
          <Toggle checked={included} onChange={setIncluded} label="" data-id="element-1891" />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50" data-id="element-1892">
        <Button variant="ghost" size="sm" onClick={onClose} data-id="element-1893">
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleSubmit} data-id="element-1894">
          Add Option
        </Button>
      </div>
    </ModalOverlay>;
}
// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
interface DeleteConfirmModalProps {
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
}
function DeleteConfirmModal({
  title,
  message,
  onClose,
  onConfirm
}: DeleteConfirmModalProps) {
  return <ModalOverlay onClose={onClose} data-id="element-1895">
      <ModalHeader title={title} onClose={onClose} data-id="element-1896" />
      <div className="px-6 py-5" data-id="element-1897">
        <p className="text-sm text-gray-600" data-id="element-1898">{message}</p>
      </div>
      <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50" data-id="element-1899">
        <Button variant="ghost" size="sm" onClick={onClose} data-id="element-1900">
          Cancel
        </Button>
        <Button variant="danger" size="sm" onClick={() => {
        onConfirm();
        onClose();
      }} data-id="element-1901">
          Delete
        </Button>
      </div>
    </ModalOverlay>;
}
// ─── Tier Card ────────────────────────────────────────────────────────────────
interface TierCardProps {
  tier: Tier;
  packageId: string;
  onEditTier: (packageId: string, tier: Tier) => void;
  onDeleteTier: (packageId: string, tierId: string) => void;
  onToggleTierActive: (packageId: string, tierId: string) => void;
  onAddOption: (packageId: string, tierId: string) => void;
  onToggleOption: (packageId: string, tierId: string, optionId: string) => void;
  onDeleteOption: (packageId: string, tierId: string, optionId: string) => void;
}
function TierCard({
  tier,
  packageId,
  onEditTier,
  onDeleteTier,
  onToggleTierActive,
  onAddOption,
  onToggleOption,
  onDeleteOption
}: TierCardProps) {
  const billingLabel = tier.billingPeriod === 'monthly' ? '/mo' : tier.billingPeriod === 'yearly' ? '/yr' : '';
  return <div className="border border-gray-200 rounded-xl overflow-hidden bg-white" data-id="element-1902">
      {/* Tier Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-1903">
        <div className="flex items-center gap-3" data-id="element-1904">
          <div className="flex items-center gap-2" data-id="element-1905">
            <span className="text-sm font-semibold text-gray-900" data-id="element-1906">
              {tier.name}
            </span>
            <Badge variant={tier.isActive ? 'success' : 'default'} data-id="element-1907">
              {tier.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          <span className="text-sm font-bold text-gray-900" data-id="element-1908">
            ${tier.price}
            <span className="text-xs font-normal text-gray-500 ml-0.5" data-id="element-1909">
              {billingLabel}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-1" data-id="element-1910">
          <Toggle checked={tier.isActive} onChange={() => onToggleTierActive(packageId, tier.id)} label="" data-id="element-1911" />
          <Button variant="ghost" size="sm" onClick={() => onEditTier(packageId, tier)} data-id="element-1912">
            <Edit2Icon size={13} data-id="element-1913" />
            <span className="ml-1" data-id="element-1914">Edit</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDeleteTier(packageId, tier.id)} data-id="element-1915">
            <Trash2Icon size={13} className="text-red-400" data-id="element-1916" />
          </Button>
        </div>
      </div>

      {/* Options List */}
      <div className="divide-y divide-gray-100" data-id="element-1917">
        {tier.options.length === 0 && <div className="px-4 py-6 text-center" data-id="element-1918">
            <p className="text-xs text-gray-400" data-id="element-1919">
              No options yet. Add one below.
            </p>
          </div>}
        {tier.options.map(option => <div key={option.id} className="flex items-center justify-between px-4 py-2.5 group" data-id="element-1920">
            <div className="flex items-center gap-3 min-w-0" data-id="element-1921">
              <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center cursor-pointer transition-colors ${option.included ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'}`} onClick={() => onToggleOption(packageId, tier.id, option.id)} title={option.included ? 'Click to exclude' : 'Click to include'} data-id="element-1922">
                <CheckIcon size={11} strokeWidth={2.5} data-id="element-1923" />
              </div>
              <div className="min-w-0" data-id="element-1924">
                <p className="text-sm text-gray-800 font-medium truncate" data-id="element-1925">
                  {option.name}
                </p>
                {option.description && <p className="text-xs text-gray-400 truncate" data-id="element-1926">
                    {option.description}
                  </p>}
              </div>
            </div>
            <button className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-300 hover:text-red-400 transition-all ml-2 flex-shrink-0" onClick={() => onDeleteOption(packageId, tier.id, option.id)} title="Remove option" data-id="element-1927">
              <XIcon size={13} data-id="element-1928" />
            </button>
          </div>)}
      </div>

      {/* Add Option Footer */}
      <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/50" data-id="element-1929">
        <button className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors" onClick={() => onAddOption(packageId, tier.id)} data-id="element-1930">
          <PlusIcon size={13} data-id="element-1931" />
          Add Option
        </button>
      </div>
    </div>;
}
// ─── Package Card ─────────────────────────────────────────────────────────────
interface PackageCardProps {
  pkg: Package;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onDeletePackage: (id: string) => void;
  onAddTier: (packageId: string) => void;
  onEditTier: (packageId: string, tier: Tier) => void;
  onDeleteTier: (packageId: string, tierId: string) => void;
  onToggleTierActive: (packageId: string, tierId: string) => void;
  onAddOption: (packageId: string, tierId: string) => void;
  onToggleOption: (packageId: string, tierId: string, optionId: string) => void;
  onDeleteOption: (packageId: string, tierId: string, optionId: string) => void;
}
function PackageCard({
  pkg,
  isExpanded,
  onToggleExpand,
  onDeletePackage,
  onAddTier,
  onEditTier,
  onDeleteTier,
  onToggleTierActive,
  onAddOption,
  onToggleOption,
  onDeleteOption
}: PackageCardProps) {
  const activeTiers = pkg.tiers.filter(t => t.isActive).length;
  return <Card className="overflow-hidden" data-id="element-1932">
      {/* Package Header */}
      <div className="flex items-center justify-between px-5 py-4 cursor-pointer select-none" onClick={onToggleExpand} data-id="element-1933">
        <div className="flex items-center gap-3" data-id="element-1934">
          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-1935">
            <LayersIcon size={16} className="text-blue-600" data-id="element-1936" />
          </div>
          <div data-id="element-1937">
            <div className="flex items-center gap-2" data-id="element-1938">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-1939">
                {pkg.name}
              </h3>
              <Badge variant="info" data-id="element-1940">
                {pkg.tiers.length} tier{pkg.tiers.length !== 1 ? 's' : ''}
              </Badge>
              {activeTiers > 0 && <Badge variant="success" data-id="element-1941">{activeTiers} active</Badge>}
            </div>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-1942">{pkg.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()} data-id="element-1943">
          <Button variant="ghost" size="sm" onClick={() => onDeletePackage(pkg.id)} data-id="element-1944">
            <Trash2Icon size={13} className="text-red-400" data-id="element-1945" />
          </Button>
          <div className="w-px h-4 bg-gray-200" data-id="element-1946" />
          <button className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" data-id="element-1947">
            {isExpanded ? <ChevronUpIcon size={16} data-id="element-1948" /> : <ChevronDownIcon size={16} data-id="element-1949" />}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && <div className="border-t border-gray-100 px-5 py-4 space-y-3 bg-gray-50/40" data-id="element-1950">
          {pkg.tiers.length === 0 && <div className="text-center py-6" data-id="element-1951">
              <PackageIcon size={24} className="text-gray-300 mx-auto mb-2" data-id="element-1952" />
              <p className="text-sm text-gray-400" data-id="element-1953">
                No tiers yet. Add your first tier.
              </p>
            </div>}

          {pkg.tiers.map(tier => <TierCard key={tier.id} tier={tier} packageId={pkg.id} onEditTier={onEditTier} onDeleteTier={onDeleteTier} onToggleTierActive={onToggleTierActive} onAddOption={onAddOption} onToggleOption={onToggleOption} onDeleteOption={onDeleteOption} data-id="element-1954" />)}

          <button className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors py-1" onClick={() => onAddTier(pkg.id)} data-id="element-1955">
            <PlusIcon size={15} data-id="element-1956" />
            Add Tier
          </button>
        </div>}
    </Card>;
}
// ─── Main Page ────────────────────────────────────────────────────────────────
export function AdminPackagesPage() {
  const [packages, setPackages] = useState<Package[]>(initialPackages);
  const [expandedPackages, setExpandedPackages] = useState<Set<string>>(new Set(['1']));
  // Modal state
  const [showCreatePackage, setShowCreatePackage] = useState(false);
  const [showAddTier, setShowAddTier] = useState<{
    packageId: string;
  } | null>(null);
  const [showEditTier, setShowEditTier] = useState<{
    packageId: string;
    tier: Tier;
  } | null>(null);
  const [showAddOption, setShowAddOption] = useState<{
    packageId: string;
    tierId: string;
  } | null>(null);
  const [showDeletePackage, setShowDeletePackage] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showDeleteTier, setShowDeleteTier] = useState<{
    packageId: string;
    tierId: string;
    name: string;
  } | null>(null);
  // ── Helpers ──────────────────────────────────────────────────────────────
  const generateId = () => Math.random().toString(36).slice(2, 9);
  const toggleExpand = (id: string) => {
    setExpandedPackages(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  // ── Package Actions ───────────────────────────────────────────────────────
  const handleCreatePackage = (name: string, description: string) => {
    const newPkg: Package = {
      id: generateId(),
      name,
      description,
      tiers: []
    };
    setPackages(prev => [...prev, newPkg]);
    setExpandedPackages(prev => new Set([...prev, newPkg.id]));
  };
  const handleDeletePackage = (id: string) => {
    setPackages(prev => prev.filter(p => p.id !== id));
  };
  // ── Tier Actions ──────────────────────────────────────────────────────────
  const handleAddTier = (packageId: string, tierData: Omit<Tier, 'id' | 'options'>) => {
    setPackages(prev => prev.map(p => p.id === packageId ? {
      ...p,
      tiers: [...p.tiers, {
        ...tierData,
        id: generateId(),
        options: []
      }]
    } : p));
  };
  const handleEditTier = (packageId: string, tierId: string, updates: Partial<Tier>) => {
    setPackages(prev => prev.map(p => p.id === packageId ? {
      ...p,
      tiers: p.tiers.map(t => t.id === tierId ? {
        ...t,
        ...updates
      } : t)
    } : p));
  };
  const handleDeleteTier = (packageId: string, tierId: string) => {
    setPackages(prev => prev.map(p => p.id === packageId ? {
      ...p,
      tiers: p.tiers.filter(t => t.id !== tierId)
    } : p));
  };
  const handleToggleTierActive = (packageId: string, tierId: string) => {
    setPackages(prev => prev.map(p => p.id === packageId ? {
      ...p,
      tiers: p.tiers.map(t => t.id === tierId ? {
        ...t,
        isActive: !t.isActive
      } : t)
    } : p));
  };
  // ── Option Actions ────────────────────────────────────────────────────────
  const handleAddOption = (packageId: string, tierId: string, optionData: Omit<PackageOption, 'id'>) => {
    setPackages(prev => prev.map(p => p.id === packageId ? {
      ...p,
      tiers: p.tiers.map(t => t.id === tierId ? {
        ...t,
        options: [...t.options, {
          ...optionData,
          id: generateId()
        }]
      } : t)
    } : p));
  };
  const handleToggleOption = (packageId: string, tierId: string, optionId: string) => {
    setPackages(prev => prev.map(p => p.id === packageId ? {
      ...p,
      tiers: p.tiers.map(t => t.id === tierId ? {
        ...t,
        options: t.options.map(o => o.id === optionId ? {
          ...o,
          included: !o.included
        } : o)
      } : t)
    } : p));
  };
  const handleDeleteOption = (packageId: string, tierId: string, optionId: string) => {
    setPackages(prev => prev.map(p => p.id === packageId ? {
      ...p,
      tiers: p.tiers.map(t => t.id === tierId ? {
        ...t,
        options: t.options.filter(o => o.id !== optionId)
      } : t)
    } : p));
  };
  // ── Context helpers for modals ────────────────────────────────────────────
  const getPackageName = (id: string) => packages.find(p => p.id === id)?.name ?? '';
  const getTierName = (packageId: string, tierId: string) => packages.find(p => p.id === packageId)?.tiers.find(t => t.id === tierId)?.name ?? '';
  const totalTiers = packages.reduce((sum, p) => sum + p.tiers.length, 0);
  const activeTiers = packages.reduce((sum, p) => sum + p.tiers.filter(t => t.isActive).length, 0);
  return <div className="min-h-screen bg-gray-50" data-id="element-1957">
      <div className="max-w-4xl mx-auto px-6 py-8" data-id="element-1958">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8" data-id="element-1959">
          <div data-id="element-1960">
            <h1 className="text-xl font-semibold text-gray-900" data-id="element-1961">Packages</h1>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-1962">
              {packages.length} package{packages.length !== 1 ? 's' : ''} ·{' '}
              {totalTiers} tier{totalTiers !== 1 ? 's' : ''} · {activeTiers}{' '}
              active
            </p>
          </div>
          <Button variant="primary" size="sm" onClick={() => setShowCreatePackage(true)} data-id="element-1963">
            <PlusIcon size={14} className="mr-1.5" data-id="element-1964" />
            Create Package
          </Button>
        </div>

        {/* Package List */}
        {packages.length === 0 ? <div className="text-center py-20" data-id="element-1965">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4" data-id="element-1966">
              <PackageIcon size={24} className="text-gray-400" data-id="element-1967" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-1968">
              No packages yet
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1969">
              Create your first package to get started.
            </p>
            <Button variant="primary" size="sm" onClick={() => setShowCreatePackage(true)} data-id="element-1970">
              <PlusIcon size={14} className="mr-1.5" data-id="element-1971" />
              Create Package
            </Button>
          </div> : <div className="space-y-3" data-id="element-1972">
            {packages.map(pkg => <PackageCard key={pkg.id} pkg={pkg} isExpanded={expandedPackages.has(pkg.id)} onToggleExpand={() => toggleExpand(pkg.id)} onDeletePackage={id => {
          const p = packages.find(x => x.id === id);
          if (p) setShowDeletePackage({
            id,
            name: p.name
          });
        }} onAddTier={packageId => setShowAddTier({
          packageId
        })} onEditTier={(packageId, tier) => setShowEditTier({
          packageId,
          tier
        })} onDeleteTier={(packageId, tierId) => {
          const tierName = getTierName(packageId, tierId);
          setShowDeleteTier({
            packageId,
            tierId,
            name: tierName
          });
        }} onToggleTierActive={handleToggleTierActive} onAddOption={(packageId, tierId) => setShowAddOption({
          packageId,
          tierId
        })} onToggleOption={handleToggleOption} onDeleteOption={handleDeleteOption} data-id="element-1973" />)}
          </div>}
      </div>

      {/* ── Modals ── */}

      {showCreatePackage && <CreatePackageModal onClose={() => setShowCreatePackage(false)} onCreate={handleCreatePackage} data-id="element-1974" />}

      {showAddTier && <AddTierModal packageName={getPackageName(showAddTier.packageId)} onClose={() => setShowAddTier(null)} onAdd={tierData => handleAddTier(showAddTier.packageId, tierData)} data-id="element-1975" />}

      {showEditTier && <EditTierModal tier={showEditTier.tier} onClose={() => setShowEditTier(null)} onSave={updates => handleEditTier(showEditTier.packageId, showEditTier.tier.id, updates)} data-id="element-1976" />}

      {showAddOption && <AddOptionModal tierName={getTierName(showAddOption.packageId, showAddOption.tierId)} onClose={() => setShowAddOption(null)} onAdd={optionData => handleAddOption(showAddOption.packageId, showAddOption.tierId, optionData)} data-id="element-1977" />}

      {showDeletePackage && <DeleteConfirmModal title="Delete Package" message={`Are you sure you want to delete "${showDeletePackage.name}"? This will remove all tiers and options within it.`} onClose={() => setShowDeletePackage(null)} onConfirm={() => handleDeletePackage(showDeletePackage.id)} data-id="element-1978" />}

      {showDeleteTier && <DeleteConfirmModal title="Delete Tier" message={`Are you sure you want to delete the "${showDeleteTier.name}" tier? All options within it will be removed.`} onClose={() => setShowDeleteTier(null)} onConfirm={() => handleDeleteTier(showDeleteTier.packageId, showDeleteTier.tierId)} data-id="element-1979" />}
    </div>;
}